/**
 * LoginPage — interim authentication for jwt_hs256 backend mode.
 *
 * The platform has no /v1/auth/login endpoint or users table yet, so this is a
 * stop-gap: the operator pastes a JWT (e.g. minted with `go run ./cmd/dev-token`)
 * which is stored in localStorage under `auth:token` and sent as the Bearer token
 * on every API request. In stub auth mode (local dev) this page is never shown —
 * the api client falls back to the dev-owner token automatically.
 */

import { useState, type FormEvent } from 'react'
import { setToken } from '../../api/client'

export interface LoginPageProps {
  /** Called with the pasted token once it has been stored. */
  onAuthenticated: (token: string) => void
}

export function LoginPage({ onAuthenticated }: LoginPageProps) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const token = value.trim()
    if (!token) {
      setError('Paste a JWT to continue.')
      return
    }
    // Minimal shape check: a JWS is three dot-separated base64url segments.
    if (token.split('.').length !== 3) {
      setError('That does not look like a JWT (expected three dot-separated parts).')
      return
    }
    setToken(token)
    onAuthenticated(token)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b1220] px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[460px] bg-[rgba(16,26,43,0.9)] border border-[rgba(148,163,184,0.12)] rounded-2xl p-8 shadow-2xl"
        aria-label="Sign in"
      >
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/assets/brand/logo.svg" alt="" className="h-7 w-7" aria-hidden="true" />
          <h1 className="text-lg font-bold text-[#e2ecf9] m-0 tracking-[-0.01em]">Sign in</h1>
        </div>

        <p className="text-[13px] text-[#4a6280] leading-[1.6] mb-5">
          This deployment uses JWT authentication. Paste a valid access token to
          continue. You can mint a development token with{' '}
          <code className="font-mono text-[12px] text-[#06b6d4]">go run ./cmd/dev-token</code>.
        </p>

        <label htmlFor="jwt" className="block text-[11px] font-bold uppercase tracking-[0.1em] text-[#4a6280] mb-1.5">
          Access token (JWT)
        </label>
        <textarea
          id="jwt"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            if (error) setError(null)
          }}
          rows={5}
          spellCheck={false}
          autoComplete="off"
          placeholder="eyJhbGciOiJIUzI1Ni..."
          className="w-full resize-y bg-[#0b1220] border border-[rgba(148,163,184,0.15)] rounded-lg px-3 py-2.5 font-mono text-xs text-[#e2ecf9] outline-none focus:border-[#06b6d4] focus:shadow-[0_0_8px_rgba(6,182,212,0.25)] break-all"
        />

        {error && (
          <p role="alert" className="text-[12px] text-[#ef4444] mt-2 mb-0">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="mt-5 w-full h-[40px] rounded-lg bg-[#06b6d4] text-[#04222b] text-sm font-bold cursor-pointer hover:bg-[#22cfe8] transition-colors"
        >
          Continue
        </button>
      </form>
    </div>
  )
}
